import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatDMY, addDays, daysBetween } from "@/lib/dateUtils";
import { overageDaySet } from "@/lib/schengenCalc";
import { cn } from "@/lib/utils";
import { useT } from "@/lib/i18n";

/**
 * Horizontal 180-day timeline. Each real trip (or gap between trips) is
 * rendered as an absolutely-positioned segment proportional to its length.
 * On hover, each segment shows its start/end date and duration.
 */
export default function Timeline180({ result, trips }) {
  const { t } = useT();
  const windowStart = result.windowStart;
  const windowEnd = result.windowEnd;
  const totalDays = daysBetween(windowStart, windowEnd) + 1;

  // Build the list of segments to render.
  const segments = useMemo(() => {
    // Normalize trips inside the window, sort by entry, then merge adjacent.
    const list = (trips || [])
      .filter((t) => t && t.entry)
      .map((t) => ({
        entry: t.entry,
        exit: t.exit || windowEnd,
        isOpen: !t.exit,
        raw: t,
      }))
      .filter((t) => t.exit >= windowStart && t.entry <= windowEnd)
      .map((t) => ({
        ...t,
        clippedEntry: t.entry < windowStart ? windowStart : t.entry,
        clippedExit: t.exit > windowEnd ? windowEnd : t.exit,
      }))
      .sort((a, b) =>
        a.clippedEntry < b.clippedEntry
          ? -1
          : a.clippedEntry > b.clippedEntry
            ? 1
            : 0,
      );

    // Overage day set for red highlight
    const overSet = overageDaySet(
      trips || [],
      windowStart,
      windowEnd,
      result.referenceDate,
    );

    const items = [];
    let cursor = windowStart;

    const pushGap = (from, to) => {
      if (from > to) return;
      items.push({
        kind: "gap",
        from,
        to,
        days: daysBetween(from, to) + 1,
      });
    };

    // Merge overlapping/adjacent stays visually
    const merged = [];
    for (const t of list) {
      const last = merged[merged.length - 1];
      if (last && t.clippedEntry <= addDays(last.clippedExit, 1)) {
        if (t.clippedExit > last.clippedExit) last.clippedExit = t.clippedExit;
        last.trips.push(t);
        last.isOpen = last.isOpen || t.isOpen;
      } else {
        merged.push({
          clippedEntry: t.clippedEntry,
          clippedExit: t.clippedExit,
          isOpen: t.isOpen,
          trips: [t],
        });
      }
    }

    for (const s of merged) {
      if (s.clippedEntry > cursor) {
        pushGap(cursor, addDays(s.clippedEntry, -1));
      }
      // Check if any day in this segment is an overage day
      let hasOver = false;
      let d = s.clippedEntry;
      while (d <= s.clippedExit) {
        if (overSet.has(d)) {
          hasOver = true;
          break;
        }
        d = addDays(d, 1);
      }
      items.push({
        kind: "stay",
        from: s.clippedEntry,
        to: s.clippedExit,
        days: daysBetween(s.clippedEntry, s.clippedExit) + 1,
        isOpen: s.isOpen,
        overage: hasOver,
        trips: s.trips.map((x) => x.raw),
      });
      cursor = addDays(s.clippedExit, 1);
    }
    if (cursor <= windowEnd) {
      pushGap(cursor, windowEnd);
    }
    return items;
  }, [trips, windowStart, windowEnd, result.referenceDate]);

  const pctOffset = (iso) => (daysBetween(windowStart, iso) / totalDays) * 100;
  const pctWidth = (days) => (days / totalDays) * 100;

  const tripLabel = (trip) => {
    const from = formatDMY(trip.entry);
    const to = trip.exit ? formatDMY(trip.exit) : t("timeline.open");
    const days = trip.exit
      ? daysBetween(trip.entry, trip.exit) + 1
      : daysBetween(trip.entry, result.referenceDate) + 1;
    return `${from} → ${to} (${days} ${t("timeline.days")})`;
  };

  // Progress %
  const usedPct = Math.min(100, (result.daysUsed / 90) * 100);
  const overPct = Math.min(100, (result.overage / 90) * 100);

  return (
    <TooltipProvider delayDuration={80} skipDelayDuration={40}>
      <Card
        className="bg-[#131620] border-[#272A35] rounded-xl p-5 md:p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] w-full h-full flex flex-col"
        data-testid="timeline-180"
      >
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-serif-display text-xl text-white tracking-tight">
            {t("timeline.title")}
          </h3>
          <span className="text-xs text-slate-400 font-mono-terminal">
            {formatDMY(windowStart)} — {formatDMY(windowEnd)}
          </span>
        </div>

        {/* Timeline track */}
        <div
          className="relative h-12 rounded-md bg-[#0C0E14] border border-[#272A35] overflow-hidden"
          data-testid="timeline-strip"
        >
          {segments.map((seg, i) => {
            const left = pctOffset(seg.from);
            const width = pctWidth(seg.days);

            if (seg.kind === "gap") {
              return (
                <Tooltip key={`g${i}`}>
                  <TooltipTrigger asChild>
                    <div
                      className="absolute top-0 bottom-0 hover:bg-slate-700/25 transition-colors cursor-help"
                      style={{
                        left: `${left}%`,
                        width: `${width}%`,
                      }}
                      data-testid={`timeline-gap-${i}`}
                      aria-label={t("timeline.tooltip.noStay")}
                    />
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="bg-[#0C0E14] border border-[#272A35] text-slate-100 px-3 py-2 shadow-xl"
                  >
                    <div className="text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-1 font-mono-terminal">
                      {formatDMY(seg.from)}
                      {seg.from !== seg.to ? ` — ${formatDMY(seg.to)}` : ""}
                    </div>
                    <div className="text-xs text-slate-300">
                      {t("timeline.tooltip.noStay")} · {seg.days}{" "}
                      {t("timeline.days")}
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            }

            const color = seg.overage
              ? "bg-red-500/85 ring-1 ring-red-300/40"
              : seg.isOpen
                ? "bg-sky-400/85"
                : "bg-[#D4AF37]";
            // Only show inline label if bar is wide enough (~5% of track)
            const showLabel = width >= 6;

            return (
              <Tooltip key={`s${i}`}>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      "absolute top-1 bottom-1 rounded-[3px] flex items-center justify-center overflow-hidden cursor-pointer transition-transform hover:scale-y-[1.08] hover:z-10",
                      color,
                    )}
                    style={{
                      left: `${left}%`,
                      width: `${width}%`,
                      minWidth: "3px",
                    }}
                    data-testid={`timeline-stay-${i}`}
                  >
                    {showLabel && (
                      <span className="text-[10px] font-mono-terminal text-black/80 font-bold px-1 truncate">
                        {seg.days}d
                      </span>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="bg-[#0C0E14] border border-[#272A35] text-slate-100 max-w-xs px-3 py-2 shadow-xl"
                >
                  <div className="text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-1 font-mono-terminal">
                    {formatDMY(seg.from)}
                    {seg.from !== seg.to ? ` — ${formatDMY(seg.to)}` : ""}
                  </div>
                  <div className="text-xs text-slate-200 mb-2 font-mono-terminal">
                    {seg.days} {t("timeline.days")}
                    {seg.isOpen ? ` · ${t("timeline.legend.active")}` : ""}
                  </div>
                  {seg.trips.length > 0 && (
                    <ul className="space-y-1 text-xs">
                      {seg.trips.map((tr, k) => (
                        <li key={k} className="flex items-start gap-1.5">
                          <span
                            className={cn(
                              "mt-1 h-1.5 w-1.5 rounded-full shrink-0",
                              !tr.exit ? "bg-sky-400" : "bg-[#D4AF37]",
                            )}
                          />
                          <span className="font-mono-terminal">
                            {tripLabel(tr)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {seg.overage && (
                    <div className="mt-1.5 text-[11px] text-red-300 flex items-center gap-1">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                      {t("timeline.tooltip.overageHere")}
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        {/* Date ticks: start, midpoint, today */}
        <div className="mt-1.5 flex justify-between text-[10px] text-slate-500 font-mono-terminal">
          <span>{formatDMY(windowStart)}</span>
          <span className="hidden sm:inline">
            {formatDMY(addDays(windowStart, Math.floor(totalDays / 2)))}
          </span>
          <span>{formatDMY(windowEnd)}</span>
        </div>

        {/* 90-day usage bar */}
        <div className="mt-5 space-y-2">
          <div className="flex justify-between text-xs text-slate-400">
            <span>{t("timeline.usageOf90")}</span>
            <span className="font-mono-terminal">
              {result.daysUsed} / 90
            </span>
          </div>
          <div className="h-2.5 rounded-full bg-[#0C0E14] border border-[#272A35] overflow-hidden relative">
            <div
              className={cn(
                "absolute inset-y-0 left-0 transition-all",
                result.status === "overage"
                  ? "bg-red-500"
                  : result.status === "warning"
                    ? "bg-amber-400"
                    : "bg-emerald-500",
              )}
              style={{ width: `${usedPct}%` }}
            />
            {overPct > 0 && (
              <div
                className="absolute inset-y-0 right-0 bg-red-500/60"
                style={{ width: `${overPct}%` }}
              />
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 flex items-center gap-4 flex-wrap text-xs text-slate-400">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-[#D4AF37]" />
            {t("timeline.legend.stay")}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-sky-400/85" />
            {t("timeline.legend.active")}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-red-500" />
            {t("timeline.legend.overage")}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-transparent border border-[#272A35]" />
            {t("timeline.legend.free")}
          </div>
        </div>
      </Card>
    </TooltipProvider>
  );
}
